/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useState, useEffect } from "react";
import styled from "@emotion/styled";
import msk from "msk";
import CountryFinderInput from "../../../CountryFinder";
import countries from "../../../../utils/countries.json";
import { Country } from "../../../../types/CountryTypes";
import StyledButton from "../../../StyledButton";
import { useRecoilState, useSetRecoilState } from "recoil";
import { UserEmailRestrictionsAtom } from "../../../../atoms/UserEmailRestrictionsAtom";
import {
  addEmailRestrictions,
  getEmailRestrictions,
  updateUser,
} from "../../../../api/User";
import { toast } from "react-toastify";
import BlackWhiteListToggle from "./BlackWhiteListToggle";
import { UserAtom } from "../../../../atoms/UserAtom";
import ReactTooltip from "react-tooltip";
import { customTooltip } from "../../../../styles/general";
import { TooltipText } from "../../../SideBar";

const CountryCodeInput = styled.input`
  width: 80px;
  height: 40px;

  padding: 0 8px;

  font-size: 16px;
  color: #2f2f2f;

  border: 1px solid #eceeef;
  border-radius: 5px 5px 5px 5px;
`;

interface CountryCodeProps {
  disabled?: boolean;
}

export default function CountryCode(props: CountryCodeProps) {
  const { disabled } = props;

  const [countryCode, setCountryCode] = useState<string>("+1");
  const [user, setUser] = useRecoilState(UserAtom);
  const [loading, setLoading] = useState(false);
  const setEmailRestrictions = useSetRecoilState(UserEmailRestrictionsAtom);

  async function handleBlockCountryCode() {
    setLoading(true);
    getEmailRestrictions();

    try {
      if (user.options.countryCodesList === "BLACKLIST") {
        await addEmailRestrictions({ countryCodesBlocked: [countryCode] });
        toast("Country code blocked", { type: "success" });
      } else {
        await addEmailRestrictions({ countryCodesWhitelisted: [countryCode] });
        toast("Country code allowed", { type: "success" });
      }
      const emailRestrictions = await getEmailRestrictions();
      setEmailRestrictions(emailRestrictions);
      setUser((u) => ({ ...u, emailRestrictions }));
    } catch {
      toast("Error", { type: "error" });
    } finally {
      setLoading(false);
    }
  }

  async function handleChangeCountry(
    selectedCountry: "BLACKLIST" | "WHITELIST"
  ) {
    try {
      await updateUser({
        options: { ...user.options, countryCodesList: selectedCountry },
      });
      setUser({
        ...user,
        options: { ...user.options, countryCodesList: selectedCountry },
      });
    } catch {
      toast("Error", { type: "error" });
    }
  }

  /**
   * Handles the country code changing both from inside the CountryFinderInput
   * and the input with the "+99..."
   *
   * If the change comes from inside the CountryFinderInput it ignores
   * the "hit country" check and just overwrites it
   *
   * @param code
   * @param force
   */
  function handleCountryCodeChange(code: string, options?: { force: boolean }) {
    // If the string is getting bigger, see if it hit a country
    // as countries have codes that are not inclusive (eg: if it
    // exists a +1 there can't be a +12), hitting a country means
    // we stop accepting more numbers into the input
    if (!options?.force && code.length > countryCode.length) {
      const country = countries.find((elem) => elem.code === `${countryCode}`);

      if (`${countryCode}` === country?.code) return;
    }
    const value = msk.fit(code, "9999");
    setCountryCode(`+${value}`);
  }

  useEffect(() => {
    const listener = (event) => {
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        event.preventDefault();
        handleBlockCountryCode();
      }
    };
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, [countryCode, user.options.countryCodesList]);

  return (
    <>
      <div data-tip data-for={"whitelist-blocklist-toggle"} className="title">
        <BlackWhiteListToggle
          disabled={disabled}
          selectedBlockCountryType={user.options.countryCodesList}
          handleChangeCountry={handleChangeCountry}
        />
      </div>

      {disabled && (
        <ReactTooltip
          id={"whitelist-blocklist-toggle"}
          place="bottom"
          effect="solid"
          backgroundColor="#333333"
          css={customTooltip}
        >
          <TooltipText style={{ textAlign: "center" }}>
            {
              "You may only enter countries in either the Whitelist or Blacklist sections.\nTo change options, please delete your current entries before proceeding."
            }
          </TooltipText>
        </ReactTooltip>
      )}

      <CountryFinderInput
        countryCode={countryCode}
        setCountryCode={() => {}}
        handleChangeCountry={(country, options) => {
          handleCountryCodeChange((country as Country).code, options);
        }}
        style={{
          height: "40px",
          borderColor: "#eceeef",
          fontSize: "16px",
        }}
      />

      <CountryCodeInput
        // onFocus={() => setDisableInput(false)}
        onChange={(event) => {
          handleCountryCodeChange(event.target.value);
        }}
        value={countryCode}
        disabled
        inputMode="tel"
        pattern="[0-9]*"
        style={{
          height: "40px",
          borderColor: "#eceeef",
          fontSize: "16px",
        }}
      />

      <StyledButton
        css={css`
          width: 112px;
          height: 40px;
        `}
        isLoading={loading}
        onClick={handleBlockCountryCode}
      >
        {user.options.countryCodesList === "BLACKLIST" ? "Block" : "Allow"}
      </StyledButton>
    </>
  );
}
